import React, { useState, useEffect } from 'react';
import axiosConfig from "../../../../api/axiosConfig";

const FilterStatisticsCard = ({ 
  selectedTeacher, 
  selectedStudent, 
  lessons = [],
  teachers = [],
  students = [],
  courses = []
}) => {
  const [teacherStats, setTeacherStats] = useState(null);
  const [studentStats, setStudentStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Öğretmen istatistiklerini hesapla
  useEffect(() => {
    if (selectedTeacher && selectedTeacher !== 'all') {
      calculateTeacherStats();
    } else {
      setTeacherStats(null);
    }
  }, [selectedTeacher, lessons]);

  // Öğrenci istatistiklerini hesapla
  useEffect(() => {
    if (selectedStudent && selectedStudent !== 'all') {
      calculateStudentStats();
    } else {
      setStudentStats(null);
    }
  }, [selectedStudent, lessons]);

  const calculateTeacherStats = async () => {
    setLoading(true);
    try {
      const teacher = teachers.find(t => String(t.id) === String(selectedTeacher));
      if (!teacher) return;

      // Bu öğretmenin derslerini filtrele
      const teacherLessons = lessons.filter(l => String(l.ogretmen_id) === String(selectedTeacher));
      
      // Ders sayıları
      const totalLessons = teacherLessons.length;
      const uniqueStudents = [...new Set(teacherLessons.map(l => l.ogrenci_id))].length;
      const totalHours = teacherLessons.reduce((sum, l) => sum + (parseInt(l.ders_sure) || 0), 0) / 60;
      
      // Finansal hesaplamalar
      const totalEarnings = teacherLessons.reduce((sum, l) => sum + (parseFloat(l.ders_ucret) || 0), 0);
      const totalCompanyShare = teacherLessons.reduce((sum, l) => sum + (parseFloat(l.sirket_payi) || 0), 0);
      const totalRevenue = teacherLessons.reduce((sum, l) => 
        sum + (parseFloat(l.ders_ucret) || 0) + 
        (parseFloat(l.sirket_payi) || 0) + 
        (parseFloat(l.danisman1) || 0) + 
        (parseFloat(l.danisman2) || 0) + 
        (parseFloat(l.danisman3) || 0), 0
      );

      // Ders dağılımı
      const courseDistribution = {};
      teacherLessons.forEach(lesson => {
        const course = courses.find(c => c.id == lesson.ders_id);
        const courseName = course ? course.ders_adi : 'Belirtilmemiş';
        courseDistribution[courseName] = (courseDistribution[courseName] || 0) + 1;
      });

      // Aylık dağılım
      const monthlyDistribution = {};
      teacherLessons.forEach(lesson => {
        if (lesson.baslangic_tarihi) {
          const date = new Date(lesson.baslangic_tarihi);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (!monthlyDistribution[monthKey]) {
            monthlyDistribution[monthKey] = {
              count: 0,
              earnings: 0,
              hours: 0
            };
          }
          monthlyDistribution[monthKey].count++;
          monthlyDistribution[monthKey].earnings += parseFloat(lesson.ders_ucret) || 0;
          monthlyDistribution[monthKey].hours += (parseInt(lesson.ders_sure) || 0) / 60;
        }
      });

      // En çok ders verdiği öğrenciler
      const studentFrequency = {};
      teacherLessons.forEach(lesson => {
        const student = students.find(s => s.id == lesson.ogrenci_id);
        const studentName = student ? student.name : 'Bilinmeyen';
        if (!studentFrequency[studentName]) {
          studentFrequency[studentName] = {
            count: 0,
            totalEarnings: 0
          };
        }
        studentFrequency[studentName].count++;
        studentFrequency[studentName].totalEarnings += parseFloat(lesson.ders_ucret) || 0;
      });

      const topStudents = Object.entries(studentFrequency)
        .sort((a, b) => b[1].count - a[1].count)
        .slice(0, 5);

      setTeacherStats({
        teacher,
        totalLessons,
        uniqueStudents,
        totalHours: totalHours.toFixed(1),
        totalEarnings,
        totalCompanyShare,
        totalRevenue,
        averageEarningsPerLesson: totalLessons > 0 ? (totalEarnings / totalLessons).toFixed(2) : 0,
        courseDistribution,
        monthlyDistribution,
        topStudents,
        status: teacher.status
      });
    } catch (error) {
      console.error('Öğretmen istatistikleri hesaplanırken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStudentStats = async () => {
    setLoading(true);
    try {
      const student = students.find(s => String(s.id) === String(selectedStudent));
      if (!student) return;

      // Bu öğrencinin derslerini filtrele
      const studentLessons = lessons.filter(l => String(l.ogrenci_id) === String(selectedStudent));
      
      // Ders sayıları
      const totalLessons = studentLessons.length;
      const uniqueTeachers = [...new Set(studentLessons.map(l => l.ogretmen_id))].length;
      const totalHours = studentLessons.reduce((sum, l) => sum + (parseInt(l.ders_sure) || 0), 0) / 60;
      
      // Finansal hesaplamalar
      const totalCost = studentLessons.reduce((sum, l) => 
        sum + (parseFloat(l.ders_ucret) || 0) + 
        (parseFloat(l.sirket_payi) || 0) + 
        (parseFloat(l.danisman1) || 0) + 
        (parseFloat(l.danisman2) || 0) + 
        (parseFloat(l.danisman3) || 0), 0
      );

      // Ders dağılımı
      const courseDistribution = {};
      studentLessons.forEach(lesson => {
        const course = courses.find(c => c.id == lesson.ders_id);
        const courseName = course ? course.ders_adi : 'Belirtilmemiş';
        courseDistribution[courseName] = (courseDistribution[courseName] || 0) + 1;
      });

      // Aylık dağılım
      const monthlyDistribution = {};
      studentLessons.forEach(lesson => {
        if (lesson.baslangic_tarihi) {
          const date = new Date(lesson.baslangic_tarihi);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (!monthlyDistribution[monthKey]) {
            monthlyDistribution[monthKey] = {
              count: 0,
              cost: 0,
              hours: 0
            };
          }
          monthlyDistribution[monthKey].count++;
          monthlyDistribution[monthKey].cost += 
            (parseFloat(lesson.ders_ucret) || 0) + 
            (parseFloat(lesson.sirket_payi) || 0) + 
            (parseFloat(lesson.danisman1) || 0) + 
            (parseFloat(lesson.danisman2) || 0) + 
            (parseFloat(lesson.danisman3) || 0);
          monthlyDistribution[monthKey].hours += (parseInt(lesson.ders_sure) || 0) / 60;
        }
      });

      // En çok ders aldığı öğretmenler
      const teacherFrequency = {};
      studentLessons.forEach(lesson => {
        const teacher = teachers.find(t => t.id == lesson.ogretmen_id);
        const teacherName = teacher ? teacher.adi : 'Bilinmeyen';
        if (!teacherFrequency[teacherName]) {
          teacherFrequency[teacherName] = {
            count: 0,
            totalCost: 0
          };
        }
        teacherFrequency[teacherName].count++;
        teacherFrequency[teacherName].totalCost += 
          (parseFloat(lesson.ders_ucret) || 0) + 
          (parseFloat(lesson.sirket_payi) || 0) + 
          (parseFloat(lesson.danisman1) || 0) + 
          (parseFloat(lesson.danisman2) || 0) + 
          (parseFloat(lesson.danisman3) || 0);
      });

      const topTeachers = Object.entries(teacherFrequency)
        .sort((a, b) => b[1].count - a[1].count)
        .slice(0, 5);

      setStudentStats({
        student,
        totalLessons,
        uniqueTeachers,
        totalHours: totalHours.toFixed(1),
        totalCost,
        averageCostPerLesson: totalLessons > 0 ? (totalCost / totalLessons).toFixed(2) : 0,
        courseDistribution,
        monthlyDistribution,
        topTeachers,
        status: student.status
      });
    } catch (error) {
      console.error('Öğrenci istatistikleri hesaplanırken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  // Ay adını Türkçe'ye çevir
  const getMonthName = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  if (!teacherStats && !studentStats) return null;

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
      {loading && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      )}

      {/* Öğretmen İstatistikleri */}
      {teacherStats && (
        <div className="relative">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {teacherStats.teacher.adi[0].toUpperCase()}
                  </span>
                </div>
                <div className="text-white">
                  <h3 className="text-2xl font-bold">{teacherStats.teacher.adi}</h3>
                  <p className="text-purple-100">{teacherStats.teacher.e_posta}</p>
                  <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-semibold ${
                    teacherStats.status === 1 || teacherStats.status === '1' 
                      ? 'bg-green-400/30 text-green-100' 
                      : teacherStats.status === 2 || teacherStats.status === '2'
                      ? 'bg-amber-400/30 text-amber-100'
                      : 'bg-red-400/30 text-red-100'
                  }`}>
                    {teacherStats.status === 1 || teacherStats.status === '1' ? 'Aktif' : 
                     teacherStats.status === 2 || teacherStats.status === '2' ? 'Beklemede' : 'Pasif'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="bg-white/20 backdrop-blur text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors"
              >
                {showDetails ? 'Detayları Gizle' : 'Detayları Göster'}
              </button>
            </div>
          </div>

          {/* Ana İstatistikler */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 p-6 bg-gradient-to-b from-gray-50 to-white">
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <div className="text-3xl font-bold text-indigo-600">{teacherStats.totalLessons}</div>
              <div className="text-sm text-gray-600 mt-1">Toplam Ders</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <div className="text-3xl font-bold text-purple-600">{teacherStats.uniqueStudents}</div>
              <div className="text-sm text-gray-600 mt-1">Farklı Öğrenci</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <div className="text-3xl font-bold text-blue-600">{teacherStats.totalHours}</div>
              <div className="text-sm text-gray-600 mt-1">Toplam Saat</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <div className="text-2xl font-bold text-green-600">₺{teacherStats.totalEarnings.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="text-sm text-gray-600 mt-1">Toplam Kazanç</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <div className="text-2xl font-bold text-orange-600">₺{teacherStats.totalCompanyShare.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="text-sm text-gray-600 mt-1">Şirket Payı</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <div className="text-2xl font-bold text-teal-600">₺{teacherStats.totalRevenue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="text-sm text-gray-600 mt-1">Toplam Ciro</div>
            </div>
          </div>

          {/* Detaylı İstatistikler */}
          {showDetails && (
            <div className="p-6 space-y-6 border-t border-gray-200">
              {/* Ders Dağılımı */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Ders Dağılımı</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {Object.entries(teacherStats.courseDistribution).map(([course, count]) => (
                    <div key={course} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200">
                      <div className="font-semibold text-blue-900">{course}</div>
                      <div className="text-2xl font-bold text-blue-600">{count}</div>
                      <div className="text-xs text-blue-700">
                        %{((count / teacherStats.totalLessons) * 100).toFixed(1)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* En Çok Ders Verdiği Öğrenciler */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">En Çok Ders Verdiği Öğrenciler</h4>
                <div className="space-y-2">
                  {teacherStats.topStudents.map(([studentName, data], index) => (
                    <div key={studentName} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {index + 1}
                        </span>
                        <div>
                          <div className="font-medium text-gray-900">{studentName}</div>
                          <div className="text-sm text-gray-500">{data.count} ders</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-green-600">₺{data.totalEarnings.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Aylık Performans */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Aylık Performans</h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 text-sm font-semibold text-gray-700">Ay</th>
                        <th className="text-center py-2 text-sm font-semibold text-gray-700">Ders</th>
                        <th className="text-center py-2 text-sm font-semibold text-gray-700">Saat</th>
                        <th className="text-right py-2 text-sm font-semibold text-gray-700">Kazanç</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(teacherStats.monthlyDistribution)
                        .sort((a, b) => b[0].localeCompare(a[0]))
                        .map(([month, data]) => (
                          <tr key={month} className="border-b border-gray-100">
                            <td className="py-2 text-sm text-gray-900">{getMonthName(month)}</td>
                            <td className="py-2 text-sm text-center font-medium">{data.count}</td>
                            <td className="py-2 text-sm text-center">{data.hours.toFixed(1)}</td>
                            <td className="py-2 text-sm text-right font-bold text-green-600">
                              ₺{data.earnings.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Öğrenci İstatistikleri */}
      {studentStats && (
        <div className="relative">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {studentStats.student.name[0].toUpperCase()}
                  </span>
                </div>
                <div className="text-white">
                  <h3 className="text-2xl font-bold">{studentStats.student.name}</h3>
                  <p className="text-teal-100">
                    {studentStats.student.phone} 
                    {studentStats.student.parent_phone && ` / ${studentStats.student.parent_phone}`}
                  </p>
                  <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-semibold ${
                    studentStats.status === 1 || studentStats.status === '1' 
                      ? 'bg-green-400/30 text-green-100' 
                      : 'bg-red-400/30 text-red-100'
                  }`}>
                    {studentStats.status === 1 || studentStats.status === '1' ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="bg-white/20 backdrop-blur text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors"
              >
                {showDetails ? 'Detayları Gizle' : 'Detayları Göster'}
              </button>
            </div>
          </div>

          {/* Ana İstatistikler */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-6 bg-gradient-to-b from-gray-50 to-white">
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <div className="text-3xl font-bold text-teal-600">{studentStats.totalLessons}</div>
              <div className="text-sm text-gray-600 mt-1">Toplam Ders</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <div className="text-3xl font-bold text-cyan-600">{studentStats.uniqueTeachers}</div>
              <div className="text-sm text-gray-600 mt-1">Farklı Öğretmen</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <div className="text-3xl font-bold text-blue-600">{studentStats.totalHours}</div>
              <div className="text-sm text-gray-600 mt-1">Toplam Saat</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <div className="text-2xl font-bold text-red-600">₺{studentStats.totalCost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="text-sm text-gray-600 mt-1">Toplam Ödeme</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <div className="text-2xl font-bold text-orange-600">₺{studentStats.averageCostPerLesson}</div>
              <div className="text-sm text-gray-600 mt-1">Ort. Ders Ücreti</div>
            </div>
          </div>

          {/* Detaylı İstatistikler */}
          {showDetails && (
            <div className="p-6 space-y-6 border-t border-gray-200">
              {/* Ders Dağılımı */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Ders Dağılımı</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {Object.entries(studentStats.courseDistribution).map(([course, count]) => (
                    <div key={course} className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-3 border border-teal-200">
                      <div className="font-semibold text-teal-900">{course}</div>
                      <div className="text-2xl font-bold text-teal-600">{count}</div>
                      <div className="text-xs text-teal-700">
                        %{((count / studentStats.totalLessons) * 100).toFixed(1)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* En Çok Ders Aldığı Öğretmenler */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">En Çok Ders Aldığı Öğretmenler</h4>
                <div className="space-y-2">
                  {studentStats.topTeachers.map(([teacherName, data], index) => (
                    <div key={teacherName} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {index + 1}
                        </span>
                        <div>
                          <div className="font-medium text-gray-900">{teacherName}</div>
                          <div className="text-sm text-gray-500">{data.count} ders</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-red-600">₺{data.totalCost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Aylık Performans */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Aylık Eğitim Özeti</h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 text-sm font-semibold text-gray-700">Ay</th>
                        <th className="text-center py-2 text-sm font-semibold text-gray-700">Ders</th>
                        <th className="text-center py-2 text-sm font-semibold text-gray-700">Saat</th>
                        <th className="text-right py-2 text-sm font-semibold text-gray-700">Tutar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(studentStats.monthlyDistribution)
                        .sort((a, b) => b[0].localeCompare(a[0]))
                        .map(([month, data]) => (
                          <tr key={month} className="border-b border-gray-100">
                            <td className="py-2 text-sm text-gray-900">{getMonthName(month)}</td>
                            <td className="py-2 text-sm text-center font-medium">{data.count}</td>
                            <td className="py-2 text-sm text-center">{data.hours.toFixed(1)}</td>
                            <td className="py-2 text-sm text-right font-bold text-red-600">
                              ₺{data.cost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterStatisticsCard;
